import type { SmsProviderConfiguration } from '@/api/notificationProviders/notificationProvidersApi.types';
import {
  SMS_SENDER_NAME_LETTER_PATTERN,
  SMS_SENDER_NAME_PATTERN,
  SMS_SENDER_PHONE_NUMBER_PATTERN,
  TWILIO_ACCOUNT_SID_PATTERN,
  TWILIO_API_KEY_SID_PATTERN,
  TWILIO_MESSAGING_SERVICE_SID_PATTERN,
} from '@/constants/smsProvider';
import { isBlank } from '@/utils/formValidation';

export type SmsProviderValidationErrorKey =
  | 'accountSidRequired'
  | 'accountSidInvalid'
  | 'apiKeySidRequired'
  | 'apiKeySidInvalid'
  | 'apiKeySecretRequired'
  | 'messagingServiceSidInvalid'
  | 'senderInvalid'
  | 'senderRequired';

export type SmsProviderFormErrors = Partial<
  Record<keyof SmsProviderConfiguration, SmsProviderValidationErrorKey>
>;

export function normalizeSmsProviderConfiguration(
  configuration: SmsProviderConfiguration
): SmsProviderConfiguration {
  return {
    accountSid: configuration.accountSid.trim(),
    apiKeySid: configuration.apiKeySid.trim(),
    apiKeySecret: configuration.apiKeySecret.trim(),
    fromNumber: configuration.fromNumber.trim(),
    messagingServiceSid: configuration.messagingServiceSid.trim(),
  };
}

export function isValidSmsSender(sender: string): boolean {
  if (SMS_SENDER_PHONE_NUMBER_PATTERN.test(sender)) {
    return true;
  }
  return SMS_SENDER_NAME_PATTERN.test(sender) && SMS_SENDER_NAME_LETTER_PATTERN.test(sender);
}

function validateAccountSid(accountSid: string): SmsProviderValidationErrorKey | undefined {
  if (isBlank(accountSid)) {
    return 'accountSidRequired';
  }
  if (TWILIO_ACCOUNT_SID_PATTERN.test(accountSid) === false) {
    return 'accountSidInvalid';
  }
  return undefined;
}

function validateApiKeySid(apiKeySid: string): SmsProviderValidationErrorKey | undefined {
  if (isBlank(apiKeySid)) {
    return 'apiKeySidRequired';
  }
  if (TWILIO_API_KEY_SID_PATTERN.test(apiKeySid) === false) {
    return 'apiKeySidInvalid';
  }
  return undefined;
}

function validateSender(
  configuration: SmsProviderConfiguration
): Pick<SmsProviderFormErrors, 'fromNumber' | 'messagingServiceSid'> {
  const hasFromNumber = isBlank(configuration.fromNumber) === false;
  const hasMessagingService = isBlank(configuration.messagingServiceSid) === false;
  if (hasFromNumber === false && hasMessagingService === false) {
    return { messagingServiceSid: 'senderRequired' };
  }
  const senderErrors: Pick<SmsProviderFormErrors, 'fromNumber' | 'messagingServiceSid'> = {};
  if (
    hasMessagingService &&
    TWILIO_MESSAGING_SERVICE_SID_PATTERN.test(configuration.messagingServiceSid) === false
  ) {
    senderErrors.messagingServiceSid = 'messagingServiceSidInvalid';
  }
  if (hasFromNumber && isValidSmsSender(configuration.fromNumber) === false) {
    senderErrors.fromNumber = 'senderInvalid';
  }
  return senderErrors;
}

export function validateSmsProviderConfiguration(
  configuration: SmsProviderConfiguration
): SmsProviderFormErrors {
  const errors: SmsProviderFormErrors = { ...validateSender(configuration) };
  const accountSidError = validateAccountSid(configuration.accountSid);
  if (accountSidError !== undefined) {
    errors.accountSid = accountSidError;
  }
  const apiKeySidError = validateApiKeySid(configuration.apiKeySid);
  if (apiKeySidError !== undefined) {
    errors.apiKeySid = apiKeySidError;
  }
  if (isBlank(configuration.apiKeySecret)) {
    errors.apiKeySecret = 'apiKeySecretRequired';
  }
  return errors;
}

export function hasSmsProviderFormErrors(errors: SmsProviderFormErrors): boolean {
  return Object.keys(errors).length > 0;
}
